/**
 * HostedAgentConfigPanel — Parameters tab for hosted GHCP agents.
 *
 * Rendered inside the Parameters tab when the selected node is a hosted agent
 * (agentModelType === 'FoundryHostedAgent'). Shows the input message field, input
 * files, and the Logic Apps tools section (the shared AgentToolsSection).
 *
 * Agent selection is handled by the Connection tab (HostedAgentConnectionTab).
 * Unlike Foundry agents, a hosted agent's model and instructions are baked into its
 * container image — but its tools are authored in the workflow like any other agent
 * and sent to the container per-invocation by the runtime.
 */

import { useCallback, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Field,
  Textarea,
  Input,
  Button,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Label,
} from '@fluentui/react-components';
import { BotSparkleFilled, AddRegular, DeleteRegular } from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useHostedAgents } from '@/hooks/useHostedAgent';
import { HostedAgentStatusBadge } from '@/components/HostedAgent/HostedAgentStatusBadge';
import { TokenizedInput } from '../fields/TokenizedInput';
import { AgentToolsSection } from '../compact/AgentToolsSection';
import { segmentsToString } from '@/types/valueSegment';
import type { ValueSegment } from '@/types/valueSegment';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  headerIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase500,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  agentInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  infoValue: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  fileInputs: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flex: 1,
  },
  removeFileRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  filesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  contentLabel: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
    marginBottom: tokens.spacingVerticalXXS,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
  },
  emptyText: {
    color: tokens.colorNeutralForeground3,
  },
});

// =============================================================================
// COMPONENT
// =============================================================================

interface HostedAgentConfigPanelProps {
  /** Workflow action name of the hosted-agent node — used to read/manage its tools. */
  agentNodeName: string;
}

export const HostedAgentConfigPanel: React.FC<HostedAgentConfigPanelProps> = ({
  agentNodeName,
}) => {
  const styles = useStyles();
  const { agents } = useHostedAgents();

  const parameterValues = useNodeEditorStore((s) => s.parameterValues);
  const setParameter = useNodeEditorStore((s) => s.setParameter);
  const isReadOnly = useNodeEditorStore((s) => s.isReadOnly);

  // Current selected agent (set from Connection tab). The durable key is
  // _modelConnection_model1 (persisted via modelConfigurations.model1.referenceName);
  // hostedAgentName is deleted on serialize for FoundryHostedAgent, so it's only
  // present during an in-memory session and gone after save+reopen.
  const selectedAgentName = (parameterValues['_modelConnection_model1']
    ?? parameterValues['hostedAgentName']
    ?? parameterValues['parameters.hostedAgentName']
    ?? '') as string;

  const selectedAgent = useMemo(
    () => agents.find((a) => a.key === selectedAgentName),
    [agents, selectedAgentName],
  );

  const handleUserMessageChange = useCallback(
    (_event: unknown, data: { value: string }) => {
      setParameter('_userMessage', data.value);
    },
    [setParameter],
  );

  const userMessage = (parameterValues['_userMessage']
    ?? parameterValues['parameters._userMessage']
    ?? '') as string;

  // Input files stored at parameters.inputFiles (direct on parameters for hosted agent).
  // content may be a plain string OR a content-envelope object ({ $content-type, $content })
  // — serialization accepts both (FoundryHostedAgentInputFile.Content is a JToken, and
  // parameterUtils keeps object envelopes). TokenizedInput edits plain text, so coerce a
  // non-string content (and name) to a string here; otherwise one externally-authored
  // workflow with an envelope would hand TokenizedInput a non-string value and crash the editor.
  const inputFiles = useMemo<Array<{ name: string; content: string }>>(() => {
    const files = parameterValues['inputFiles'];
    if (!Array.isArray(files)) return [];
    return (files as Array<{ name?: unknown; content?: unknown }>).map((f) => ({
      name: typeof f?.name === 'string' ? f.name : '',
      content:
        typeof f?.content === 'string'
          ? f.content
          : f?.content == null
            ? ''
            : JSON.stringify(f.content, null, 2),
    }));
  }, [parameterValues]);

  const handleAddFile = useCallback(() => {
    const updated = [...inputFiles, { name: '', content: '' }];
    setParameter('inputFiles', updated);
  }, [inputFiles, setParameter]);

  const handleRemoveFile = useCallback((index: number) => {
    const updated = inputFiles.filter((_, i) => i !== index);
    setParameter('inputFiles', updated);
  }, [inputFiles, setParameter]);

  const handleFileNameChange = useCallback((index: number, name: string) => {
    const updated = inputFiles.map((f, i) => i === index ? { ...f, name } : f);
    setParameter('inputFiles', updated);
  }, [inputFiles, setParameter]);

  const handleFileContentChange = useCallback((index: number, segments: ValueSegment[]) => {
    const content = segmentsToString(segments);
    const updated = inputFiles.map((f, i) => i === index ? { ...f, content } : f);
    setParameter('inputFiles', updated);
  }, [inputFiles, setParameter]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <BotSparkleFilled aria-hidden="true" className={styles.headerIcon} />
        <Text weight="semibold" size={400}>
          Agent (Hosted)
        </Text>
      </div>

      {/* Selected agent info card (read-only summary of what's picked in Connection tab) */}
      {selectedAgent && (
        <div className={styles.agentInfo}>
          <div className={styles.infoRow}>
            <Text className={styles.infoLabel}>Agent</Text>
            <Text className={styles.infoValue}>{selectedAgent.displayName}</Text>
          </div>
          <div className={styles.infoRow}>
            <Text className={styles.infoLabel}>Model</Text>
            <Text className={styles.infoValue}>{selectedAgent.configuration.modelDeployment}</Text>
          </div>
          <div className={styles.infoRow}>
            <Text className={styles.infoLabel}>Project</Text>
            <Text className={styles.infoValue}>{selectedAgent.configuration.projectName}</Text>
          </div>
          <div className={styles.infoRow}>
            <Text className={styles.infoLabel}>Status</Text>
            <HostedAgentStatusBadge status={selectedAgent.status} />
          </div>
        </div>
      )}

      {/* Input message */}
      <div className={styles.section}>
        <Field label="Input message">
          <Textarea
            placeholder="Enter the message to send to the hosted agent..."
            value={userMessage}
            onChange={handleUserMessageChange}
            resize="vertical"
            rows={4}
            disabled={isReadOnly}
          />
        </Field>
      </div>

      {/* Input Files */}
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>Input Files</Text>
        {inputFiles.length === 0 && (
          <Text italic size={200} className={styles.emptyText}>
            No items. Click &quot;Add Item&quot; to add one.
          </Text>
        )}
        {inputFiles.length > 0 && (
          <Accordion multiple collapsible defaultOpenItems={inputFiles.map((_, i) => `file-${i}`)}>
            {inputFiles.map((file, index) => (
              <AccordionItem key={index} value={`file-${index}`}>
                <AccordionHeader>
                  <Text weight="semibold" size={300}>Item {index + 1}</Text>
                </AccordionHeader>
                <AccordionPanel>
                  <div className={styles.fileInputs}>
                    <Field label="File Name" size="small" required>
                      <Input
                        placeholder="Name of the file in the agent workspace (e.g., data.csv, report.pdf)"
                        value={file.name}
                        onChange={(_e, data) => handleFileNameChange(index, data.value)}
                        disabled={isReadOnly}
                      />
                    </Field>
                    <div>
                      <div className={styles.contentLabel}>
                        <Label size="small">Content</Label>
                        {/* Decorative marker: requiredness is conveyed via the
                            TokenizedInput accessible name, so hide * from AT. */}
                        <span className={styles.required} aria-hidden="true">*</span>
                      </div>
                      <TokenizedInput
                        name={`inputFile-content-${index}`}
                        value={file.content || ''}
                        onChange={(segments) => handleFileContentChange(index, segments)}
                        placeholder="Use expressions to reference previous action outputs (e.g., @{body('Get_Invoice')})"
                        ariaLabel={`Content for input file ${file.name || index + 1} (required)`}
                        multiline
                        disabled={isReadOnly}
                      />
                    </div>
                    {/* Remove lives in the panel (not the AccordionHeader button)
                        to avoid nesting an interactive control inside a <button>. */}
                    <div className={styles.removeFileRow}>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DeleteRegular />}
                        aria-label={`Remove file ${file.name || index + 1}`}
                        disabled={isReadOnly}
                        onClick={() => handleRemoveFile(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        )}
        <Button
          appearance="secondary"
          size="small"
          icon={<AddRegular />}
          onClick={handleAddFile}
          disabled={isReadOnly}
        >
          Add Item
        </Button>
      </div>

      {/* Tools — same functional tools UI as normal agents. Hosted agents support
          workflow-authored tools (incl. MCP); the runtime sends them per-invocation. */}
      <AgentToolsSection agentName={agentNodeName} readOnly={isReadOnly} />
    </div>
  );
};
